import { CheckIcon, StarIcon } from "@heroicons/react/solid";
import logoDev from "@static/img/topicflow-logo-dev.png";
import logo from "@static/img/topicflow-logo.svg";
import { ReactNode } from "react";

import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

export type PanelCard = { icon: ReactNode; title: string; text: string };

// Defaults (used on the sign-in page); the wizard overrides these per step.
const DEFAULT_HEADING = "Stop managing from memory.";
const DEFAULT_SUBTEXT =
  "Topicflow remembers what matters across your team — so you know what changed, who needs help, and what to do next.";

// The signature gradient from topicflow.com: blue → sky → purple (no pink).
// Applied as clipped text on the headline, matching the marketing site hero.
const HEADLINE_GRADIENT =
  "linear-gradient(90deg, #2563eb 0%, #1a91ff 30%, #7c3aed 70%)";
// The subtle top-down wash the marketing hero uses, fading to white.
export const PANEL_WASH =
  "linear-gradient(180deg, rgba(124,58,237,0.06) 0%, rgba(26,145,255,0.08) 45%, rgba(255,255,255,0) 80%)";
// The product benefits from topicflow.com, shown between the headline and the
// proof strip.
const BENEFITS = [
  "Never lose track of your team",
  "Walk into every 1:1 prepared",
  "Never dread review season again",
];

/**
 * The onboarding right-hand brand panel. Combines the longer product promise
 * with a proof strip (rating + integrations + compliance). Copy, ratings and
 * gradient styling are pulled from topicflow.com.
 */
const OnboardingBrandPanel = ({
  className,
  cards,
  heading = DEFAULT_HEADING,
  subtext = DEFAULT_SUBTEXT,
}: {
  className?: string;
  cards?: PanelCard[];
  heading?: string;
  subtext?: string;
}) => (
  <aside
    className={classNames(
      "flex-col justify-between bg-white px-10 py-12",
      className,
    )}
    style={{ backgroundImage: PANEL_WASH }}
  >
    <div className="flex flex-col gap-8">
      <img
        src={getAssetUrl(import.meta.env.DEV ? logoDev : logo)}
        alt="Topicflow"
        className="block h-7 w-auto self-start"
      />
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <h2
            className="bg-clip-text font-signika text-[20px] font-semibold leading-[1.1] text-transparent lg:text-2xl"
            style={{ backgroundImage: HEADLINE_GRADIENT }}
          >
            {heading}
          </h2>
          <p className="max-w-[320px] text-base leading-6 text-gray-600">
            {subtext}
          </p>
        </div>
        {cards && cards.length > 0 ? (
          <div className="flex flex-col gap-3">
            {cards.map((card) => (
              <div
                key={card.title}
                className="flex gap-2.5 rounded-xl bg-white/60 p-3.5"
              >
                <span className="mt-0.5 shrink-0 text-blue-600">
                  {card.icon}
                </span>
                <div>
                  <div className="text-[15px] font-semibold leading-5 text-gray-800">
                    {card.title}
                  </div>
                  <p className="mt-1 text-[15px] leading-5 text-gray-600">
                    {card.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
                  <CheckIcon className="h-3 w-3" />
                </span>
                <span className="text-[15px] leading-5 text-gray-700">
                  {benefit}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>

    <div className="flex flex-col gap-2 border-t border-gray-200 pt-5 text-sm text-gray-500">
      <div className="flex items-center gap-2">
        <StarIcon className="h-4 w-4 text-amber-400" />
        4.8/5 on Capterra
      </div>
      <div className="flex items-center gap-2">
        <span>200+ integrations</span>
        <span className="text-gray-300">•</span>
        <span>SOC 2 certified</span>
      </div>
    </div>
  </aside>
);

export default OnboardingBrandPanel;
